import { Spinner } from '@/component'
import { cn } from '@/lib/utils'
import { Slot } from '@radix-ui/react-slot'
import React from 'react'
import { type HTMLAttributes } from 'react'
import { useRequestInViewport } from '@/hook/use-request-in-viewport'
import { type Result } from 'ahooks/lib/useRequest/src/types'
import { Empty as EmptyCard, ErrorCard } from '@/bc'
export type PanelContextOption = Partial<ReturnType<typeof useRequestInViewport> & { empty?: boolean }>
const PanelContext = React.createContext<PanelContextOption>({})
const usePanel = <T,>() => React.useContext(PanelContext) as Partial<Result<T, unknown[]> & { empty?: boolean }>

type RootProps = HTMLAttributes<HTMLDivElement> & Pick<PanelContextOption, 'loading' | 'error' | 'empty'>
type AsChild = { asChild?: boolean }

/**
 * @example
 * <Panel.Root loading={loading} error={error}>
 *  <Panel.Title>Title</Panel.Title>
 *  <Panel.Content>
 *  ...
 *  <Panel.Content>
 *  <Panel.Loading />
 *  <Panel.Error />
 * </Panel.Root>
 * @returns
 */
const Root = React.forwardRef<HTMLDivElement, RootProps>(({ className, loading, error, empty, ...rest }: RootProps, ref) => {
  return (
    <PanelContext.Provider value={{ loading, error, empty }}>
      <div
        className={cn('flex flex-col rounded-xl bg-background py-6 shadow-panel relative', className)}
        {...rest}
        ref={ref}
      ></div>
    </PanelContext.Provider>
  )
})

function Title({ className, border, ...rest }: React.ComponentProps<'div'> & { border?: boolean }) {
  return (
    <div className="relative">
      <div
        className={cn(
          '-order-2 w-full px-6 text-xl font-bold leading-none text-foreground',
          {
            'before:absolute before:left-0 before:top-[50%] before:-translate-y-1/2 before:w-0.75 before:h-2 before:bg-primary':
              border,
          },
          className,
        )}
        {...rest}
      />
    </div>
  )
}

function SubTitle({ className, ...rest }: React.ComponentProps<'div'>) {
  return <div className={cn('-order-1 mt-2 w-full px-6 text-sm leading-none text-muted-foreground-2', className)} {...rest} />
}
function Loading({ asChild, className, ...props }: React.ComponentProps<typeof Spinner> & AsChild) {
  const Com = asChild ? Slot : Spinner
  const { loading } = usePanel()
  if (!loading) return null
  return <Com {...props} className={cn('absolute w-full h-full bg-mask-light z-20', className)} />
}
function Error({ asChild, className, ...rest }: React.ComponentProps<typeof ErrorCard> & AsChild) {
  const { loading, error, empty } = usePanel()
  if (loading || !error || empty) return null
  const Com = asChild ? Slot : ErrorCard
  return <Com {...rest} className={cn('py-5', className)} />
}

function Empty({ asChild, className, ...rest }: React.ComponentProps<typeof ErrorCard> & AsChild) {
  const { loading, error, empty } = usePanel()

  if (loading || error || !empty) return null
  const Com = asChild ? Slot : EmptyCard
  return <Com {...rest} className={cn('py-5', className)} />
}

function Content({ className, ...rest }: React.ComponentProps<'div'>) {
  const { error, empty } = usePanel()
  if (error || empty) return null
  return <div className={cn('order-1 px-6', className)} {...rest} />
}
export { Root, Title, SubTitle, Loading, Error, Content, Empty, usePanel }
